import type { Shipment } from '@modules/Invoices/InvoicesOrders/List/models/Shipments';
import type { StockMovementsModel } from '@modules/Reports/StockMovements/models/StockMovements';
import { Product } from '@shared/components/ui/Product';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useOrderInvoicedShipments } from '../../hooks/useOrderInvoicedShipments';
import { useOrderStockMovements } from '../../hooks/useOrderStockMovements';

interface OrderStockMovementsProps {
  orderId?: number;
}

type OrderActivityTab = 'movements' | 'billing';

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const getDisplayQuantity = (movement: StockMovementsModel): number => {
  const qty = movement.qty ?? 0;
  if (movement.from_type_id === 1 && movement.to_type_id !== 1) {
    return -qty;
  }
  return qty;
};

const getShippingDate = (shipment: Shipment): string | null => {
  const date = shipment.shipping_date?.date;
  return date ? formatDateWithTime(date) : null;
};

export const OrderStockMovements: FC<OrderStockMovementsProps> = ({ orderId }) => {
  const { t } = useTranslation('viewOrdersModule');
  const [activeTab, setActiveTab] = useState<OrderActivityTab>('movements');

  const {
    data: movementsData,
    isLoading: isLoadingMovements,
    error: movementsError,
  } = useOrderStockMovements(orderId, activeTab === 'movements');

  const {
    data: billingData,
    isLoading: isLoadingBilling,
    error: billingError,
  } = useOrderInvoicedShipments(orderId, activeTab === 'billing');

  const movements = movementsData?.stockmovements ?? [];
  const shipments = billingData?.shipments ?? [];

  const billingRows = useMemo(
    () =>
      shipments.flatMap((shipment) =>
        (shipment.lines ?? []).map((line) => ({
          key: `${shipment.id}-${line.id}`,
          shipment,
          line,
        })),
      ),
    [shipments],
  );

  const getShipmentTypeLabel = (type: Shipment['type']) => t(`sections.billing.shipment-types.${type}`, type);

  const getConceptLabel = (concept: string) => t(`sections.billing.concepts.${concept}`, concept);

  return (
    <SectionBox className="bg-card-bg">
      <h2 className="text-lg font-bold">{t('sections.stock-movements.title')}</h2>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as OrderActivityTab)} className="pt-4">
        <TabsList>
          <TabsTrigger value="movements">{t('sections.stock-movements.tabs.movements')}</TabsTrigger>
          <TabsTrigger value="billing">{t('sections.stock-movements.tabs.billing')}</TabsTrigger>
        </TabsList>

        <TabsContent value="movements" className="mt-4">
          {isLoadingMovements && <p className="text-sm text-subtext">{t('sections.stock-movements.loading')}</p>}

          {movementsError && <p className="text-sm text-error">{t('sections.stock-movements.error')}</p>}

          {!isLoadingMovements && !movementsError && movements.length === 0 && (
            <p className="text-sm text-subtext">{t('sections.stock-movements.empty')}</p>
          )}

          {!isLoadingMovements && !movementsError && movements.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="border-card-border hover:bg-transparent">
                  <TableHead className="text-xs text-complementary-text">
                    {t('sections.stock-movements.product')}
                  </TableHead>
                  <TableHead className="text-xs text-complementary-text">
                    {t('sections.stock-movements.type')}
                  </TableHead>
                  <TableHead className="text-xs text-complementary-text text-right">
                    {t('sections.stock-movements.quantity')}
                  </TableHead>
                  <TableHead className="text-xs text-complementary-text">
                    {t('sections.stock-movements.date')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((movement) => {
                  const displayQty = getDisplayQuantity(movement);
                  const isNegative = displayQty < 0;

                  return (
                    <TableRow key={movement.id} className="border-card-border">
                      <TableCell>
                        {movement.product?.name ? (
                          <Product
                            id={movement.product.id}
                            src={movement.product.image || ''}
                            name={movement.product.name}
                            code={movement.product.code || ''}
                            enableLineClamp
                            showImageModal
                          />
                        ) : (
                          <span className="text-sm text-subtext">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{movement.event?.name || '-'}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`text-sm font-medium ${isNegative ? 'text-error' : 'text-success'}`}>
                          {isNegative ? displayQty : `+${displayQty}`}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-complementary-text whitespace-nowrap">
                          {movement.created_at ? formatDateWithTime(movement.created_at) : '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          {isLoadingBilling && <p className="text-sm text-subtext">{t('sections.billing.loading')}</p>}

          {billingError && <p className="text-sm text-error">{t('sections.billing.error')}</p>}

          {!isLoadingBilling && !billingError && billingRows.length === 0 && (
            <p className="text-sm text-subtext">{t('sections.billing.empty')}</p>
          )}

          {!isLoadingBilling && !billingError && billingRows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="border-card-border hover:bg-transparent">
                  <TableHead className="text-xs text-complementary-text">{t('sections.billing.type')}</TableHead>
                  <TableHead className="text-xs text-complementary-text">{t('sections.billing.concept')}</TableHead>
                  <TableHead className="text-xs text-complementary-text text-right">
                    {t('sections.billing.amount')}
                  </TableHead>
                  <TableHead className="text-xs text-complementary-text">{t('sections.billing.invoice')}</TableHead>
                  <TableHead className="text-xs text-complementary-text">{t('sections.billing.date')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billingRows.map(({ key, shipment, line }) => {
                  const invoiceLabel =
                    shipment.invoice?.erp_number ||
                    shipment.invoice?.invoice_number ||
                    (shipment.invoice_id ? `#${shipment.invoice_id}` : '-');
                  const amount = line.base_amount ?? line.total_amount ?? 0;

                  return (
                    <TableRow key={key} className="border-card-border">
                      <TableCell>
                        <span className="text-sm">{getShipmentTypeLabel(shipment.type)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{getConceptLabel(line.concept)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-medium tabular-nums">{formatAmount(amount)} €</span>
                      </TableCell>
                      <TableCell>
                        {shipment.invoice_id ? (
                          <Link
                            to={`/invoices/view/${shipment.invoice_id}`}
                            className="text-sm font-medium text-title underline underline-offset-2"
                          >
                            {invoiceLabel}
                          </Link>
                        ) : (
                          <span className="text-sm text-subtext">{invoiceLabel}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-complementary-text whitespace-nowrap">
                          {getShippingDate(shipment) ?? '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </SectionBox>
  );
};
